import { Link } from "react-router-dom";
import SEO from "@/components/base/SEO";
import SchemaOrg from "@/components/base/SchemaOrg";
import { heroData, homeFeaturedWorks, homeFeaturedArticles, galleryImages, faqData } from "@/mocks/home";
import { works } from "@/mocks/works";
import { articles } from "@/mocks/articles";
import { siteConfig } from "@/mocks/site";
import { aboutContent } from "@/mocks/about";

function HomeSchema() {
  const orgSchema = {
    "@type": "Organization",
    name: siteConfig.name,
    url: siteConfig.url,
    description: siteConfig.description,
  };

  const websiteSchema = {
    "@type": "WebSite",
    name: siteConfig.name,
    url: siteConfig.url,
    description: siteConfig.description,
  };

  const faqSchema = {
    "@type": "FAQPage",
    mainEntity: faqData.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <SchemaOrg type="Organization" data={orgSchema} />
      <SchemaOrg type="WebSite" data={websiteSchema} />
      <SchemaOrg type="FAQPage" data={faqSchema} />
    </>
  );
}

export default function Home() {
  const featuredWorksList = works.filter((w) => homeFeaturedWorks.includes(w.id));
  const featuredArticlesList = articles.filter((a) => homeFeaturedArticles.includes(a.id));

  return (
    <>
      <SEO
        title={siteConfig.title}
        description={siteConfig.description}
        keywords={siteConfig.keywords}
        canonicalUrl={siteConfig.url}
        ogType="website"
        ogImage={siteConfig.ogImage}
      />
      <HomeSchema />

      {/* Hero Section */}
      <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
        <img
          src={heroData.image}
          alt="风间由美"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>
        <div className="relative z-10 w-full h-full flex flex-col items-center justify-center text-center px-4">
          <h1 className="text-3xl md:text-5xl font-semibold text-white mb-3" style={{ fontFamily: "var(--font-heading)" }}>
            {heroData.title}
          </h1>
          <p className="text-lg md:text-xl text-white/90 mb-2">{heroData.subtitle}</p>
          <p className="text-sm md:text-base text-white/70 max-w-xl mb-8">{heroData.description}</p>
          <Link
            to={heroData.ctaLink}
            className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-6 py-3 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            {heroData.ctaText}
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-background-100 border-b border-background-200/60">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {aboutContent.stats.map((stat) => (
              <div key={stat.label}>
                <p className="text-2xl md:text-3xl font-semibold text-primary-500" style={{ fontFamily: "var(--font-heading)" }}>
                  {stat.value}
                </p>
                <p className="text-sm text-foreground-600 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Latest News */}
      <section className="py-14 md:py-18">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-semibold text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
                最新动态
              </h2>
              <p className="text-sm text-foreground-600 mt-1">了解风间由美的最新消息与活动资讯</p>
            </div>
            <Link
              to="/category/all"
              className="text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
            >
              查看全部 <i className="ri-arrow-right-s-line"></i>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {featuredArticlesList.map((article, index) => (
              <Link
                key={article.id}
                to={`/article/${article.id}`}
                className="group cursor-pointer bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-300 transition-all"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    title={`风间由美 ${article.title}`}
                  />
                </div>
                <div className="p-4 md:p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-xs text-accent-500 bg-accent-100 px-2 py-0.5 rounded-md whitespace-nowrap">
                      {article.tags[0]}
                    </span>
                    <span className="text-xs text-foreground-500">{article.date}</span>
                  </div>
                  <h3 className="text-base font-medium text-foreground-950 leading-snug mb-2 group-hover:text-primary-500 transition-colors line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                    {article.summary}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Works */}
      <section className="py-14 md:py-18 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-semibold text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
                精选作品
              </h2>
              <p className="text-sm text-foreground-600 mt-1">风间由美演艺生涯代表作品</p>
            </div>
            <Link
              to="/category/all"
              className="text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
            >
              全部作品 <i className="ri-arrow-right-s-line"></i>
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
            {featuredWorksList.map((work) => (
              <Link
                key={work.id}
                to={`/category/${work.category}`}
                className="group cursor-pointer bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all"
              >
                <div className="relative aspect-[3/4] overflow-hidden">
                  <img
                    src={work.image}
                    alt={`风间由美 ${work.title}`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    title={`风间由美 ${work.title}`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                    <div>
                      <p className="text-white text-sm font-medium">{work.role}</p>
                      <p className="text-white/80 text-xs">{work.year}</p>
                    </div>
                  </div>
                </div>
                <div className="p-3">
                  <h3 className="text-sm font-medium text-foreground-950 truncate">{work.title}</h3>
                  <p className="text-xs text-foreground-500 mt-0.5">{work.year} · {work.role}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-14 md:py-18">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-semibold text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
              精彩图集
            </h2>
            <p className="text-sm text-foreground-600 mt-1">风间由美写真、活动与幕后瞬间</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {galleryImages.map((img) => (
              <div
                key={img.id}
                className="relative aspect-[3/2] overflow-hidden rounded-lg cursor-pointer group"
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  title={img.alt}
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fan Zone CTA */}
      <section className="relative py-20 md:py-24 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Japanese%20actress%20waving%20to%20fans%2C%20warm%20golden%20backlighting%2C%20concert%20venue%20atmosphere%2C%20emotional%20moment%2C%20soft%20bokeh%20background%2C%20heartwarming%20scene%2C%20cinematic%20photography&width=1600&height=600&seq=cta-bg&orientation=landscape"
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/60"></div>
        <div className="relative z-10 max-w-3xl mx-auto text-center px-4">
          <h2 className="text-2xl md:text-3xl font-semibold text-white mb-3" style={{ fontFamily: "var(--font-heading)" }}>
            加入粉丝俱乐部
          </h2>
          <p className="text-white/80 text-sm md:text-base mb-8 max-w-xl mx-auto">
            成为50万粉丝中的一员，获取最新动态、专属内容和限量周边
          </p>
          <Link
            to="/about"
            className="inline-flex items-center gap-2 bg-accent-500 text-background-50 px-6 py-3 rounded-md text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            了解更多 <i className="ri-heart-line"></i>
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 md:py-18 bg-background-100">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-semibold text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
              常见问题
            </h2>
          </div>
          <div className="flex flex-col gap-3">
            {faqData.map((faq, index) => (
              <details
                key={index}
                className="group bg-background-50 border border-background-200/70 rounded-lg cursor-pointer"
              >
                <summary className="px-5 py-4 text-sm font-medium text-foreground-950 list-none flex items-center justify-between">
                  {faq.question}
                  <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform"></i>
                </summary>
                <p className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}